import type {
	AddressStats,
	CityContext,
	MacroregionContext,
	MappedLunrSearchResult,
	RegionContext,
	SearchAddressesResult
} from '~/types/address-context';
import type {
	Address,
	AddressData,
	Addresses
} from '~~/core/entities/Addresses';

import { AddressUtils } from '@/utils/AddressUtilsWrapper';
import { useCityRegistry } from '~/composables/useCityRegistry';
import { useAddressSearchService } from '~/services/addressSearchService';
import { AddressUtilsMultiCity } from '~/utils/AddressUtils';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt/nuxtPropertiesRepository';

/*
    TODO: Escrever testes unitários para este arquivo o quanto antes
*/

const DEFAULT_ADDRESSES: Addresses[] = [];

async function getAddresses() {
	try {
		const [result, error] = await nuxtPropertiesRepository.getAddresses();

		if (error || !result) {
			return DEFAULT_ADDRESSES;
		}

		return result.data;
	} catch {
		return DEFAULT_ADDRESSES;
	}
}

/**
 * Funções stub para o servidor (retornam valores vazios/default)
 */
const SERVER_STUB_FUNCTIONS = {
	getStateByCity: () => undefined,
	getMacroregionByCity: () => undefined,
	getRegionInfo: () => undefined,
	formatAddressDisplay: () => '',
	isValidAddress: () => false,
	getRegionContext: () => null,
	getCityContext: () => null,
	getMacroregionContext: () => null,
	getAddressStats: () => ({
		totalStates: 0,
		totalCities: 0,
		totalMacroregions: 0,
		totalRegions: 0
	}),
	searchAddresses: () => ({
		regions: [],
		cities: [],
		macroregions: [],
		states: []
	}),
	getAllStates: () => [],
	getCitiesByState: () => [],
	getMacroregionsByState: () => [],
	getRegionsByState: () => [],
	getRegionsByCity: () => [],
	getRegionsByMacroregion: () => []
} as const;

interface UseAddressesReturn {
	addresses: Ref<Addresses[]>;
	state: ComputedRef<string>;
	macroregions: ComputedRef<Address[]>;
	cities: ComputedRef<Address[]>;
	regions: ComputedRef<AddressData[]>;
	filteredRegions: ComputedRef<MappedLunrSearchResult[]>;
	filteredCities: ComputedRef<Address[]>;

	getStateByCity: (city: string) => string | undefined;
	getMacroregionByCity: (state: string, city: string) => string | undefined;
	getRegionInfo: (
		state: string,
		regionLabel: string
	) => AddressData | undefined;
	formatAddressDisplay: (
		region?: string,
		city?: string,
		state?: string,
		macroregion?: string
	) => string;
	isValidAddress: (
		state?: string,
		city?: string,
		region?: string,
		macroregion?: string
	) => boolean;
	getRegionContext: (
		state: string,
		regionLabel: string
	) => RegionContext | null;
	getCityContext: (state: string, city: string) => CityContext | null;
	getMacroregionContext: (
		state: string,
		macroregion: string
	) => MacroregionContext | null;
	getAddressStats: () => AddressStats;
	searchAddresses: (searchTerm: string) => SearchAddressesResult;
	getAllStates: () => string[];
	getCitiesByState: (state: string) => Address[];
	getMacroregionsByState: (state: string) => Address[];
	getRegionsByState: (state: string) => AddressData[];
	getRegionsByCity: (state: string, city: string) => AddressData[];
	getRegionsByMacroregion: (
		state: string,
		macroregion: string
	) => AddressData[];
}

export function useAddressesMultiCityInternal(
	searchTerm?: Ref<string>
): UseAddressesReturn {
	// No servidor, retorna valores vazios imediatamente
	// Os dados de endereços são usados apenas no cliente (dentro de <ClientOnly>)
	if (import.meta.server) {
		return {
			addresses: shallowRef(DEFAULT_ADDRESSES),
			state: computed(() => ''),
			macroregions: computed(() => []),
			cities: computed(() => []),
			regions: computed(() => []),
			filteredRegions: computed(() => []),
			filteredCities: computed(() => []),
			...SERVER_STUB_FUNCTIONS
		} as UseAddressesReturn;
	}

	const addresses = useState<Addresses[]>(
		'addresses-multi-city',
		() => DEFAULT_ADDRESSES
	);

	// Flag para evitar inicializações duplicadas
	const isInitialized = useState<boolean>(
		'addresses-multi-city-initialized',
		() => false
	);

	// Serviço de busca centralizado
	const searchService = useAddressSearchService();

	const addressesFetch = useAsyncData<Addresses[]>(
		`properties-addresses`,
		async () => {
			if (addresses.value.length) return addresses.value;

			const newAddresses = await getAddresses();

			return newAddresses;
		},
		{
			default: () => DEFAULT_ADDRESSES,
			server: false // Não busca no servidor
		}
	);

	const { getMergedAddresses } = useCityRegistry();

	// Cache do resultado de getMergedAddresses para evitar recálculos
	const cachedMergedAddresses = useState<Addresses[]>(
		'addresses-multi-city-merged-cache',
		() => DEFAULT_ADDRESSES
	);

	// Atualiza addresses quando os dados do fetch mudam
	const stopWatchEffect = watchEffect(() => {
		const newAddresses = addressesFetch.data.value || DEFAULT_ADDRESSES;

		// Só atualiza se os dados realmente mudaram
		if (addresses.value !== newAddresses) {
			addresses.value = newAddresses;
		}
	});

	// Watch para atualizar o cache quando addresses mudar
	const stopWatchAddresses = watch(
		addresses,
		newAddresses => {
			if (newAddresses?.length) {
				cachedMergedAddresses.value = getMergedAddresses(newAddresses);
			} else {
				cachedMergedAddresses.value = DEFAULT_ADDRESSES;
			}
		},
		{ immediate: true }
	);

	// Computed puro que apenas retorna o valor cacheado
	const mergedAddresses = computed<Addresses[]>(
		() => cachedMergedAddresses.value
	);

	const state = computed<string>(() => mergedAddresses.value[0]?.state || '');
	const macroregions = computed<Address[]>(() =>
		mergedAddresses.value.flatMap(addr => addr.macroregions || [])
	);
	const cities = computed<Address[]>(() =>
		mergedAddresses.value.flatMap(addr => addr.cities || [])
	);
	const regions = computed<AddressData[]>(() =>
		mergedAddresses.value.flatMap(addr =>
			(addr.regions || []).map(region => ({
				...region,
				data: {
					...region.data,
					state: addr.state
				}
			}))
		)
	);

	// Inicializa os índices de busca quando os dados estão prontos
	const stopWatchIndices = watch(
		[regions, cities, mergedAddresses],
		async ([newRegions, newCities, newAddresses]) => {
			if (newAddresses.length > 0) {
				// Inicializa AddressUtils apenas uma vez
				if (!isInitialized.value) {
					AddressUtilsMultiCity.initialize(newAddresses);
					AddressUtils.initializeFromAddresses(newAddresses);
					isInitialized.value = true;
				}

				// Atualiza os índices de busca (o serviço verifica internamente se precisa recriar)
				await searchService.updateIndices(
					'multi-city',
					newRegions,
					newCities,
					newAddresses
				);
			}
		},
		{ immediate: true }
	);

	// Cleanup dos watchers quando o escopo do componente é destruído
	if (getCurrentScope()) {
		onScopeDispose(() => {
			stopWatchEffect();
			stopWatchAddresses();
			stopWatchIndices();
		});
	}

	// Computed que usa o serviço de busca
	const filteredRegions = computed(() => {
		return searchService.searchRegions(
			'multi-city',
			searchTerm?.value
		) as MappedLunrSearchResult[];
	});

	const filteredCities = computed(() => {
		return searchService.searchCities('multi-city', searchTerm?.value);
	});

	return {
		addresses,
		state,
		macroregions,
		cities,
		regions,
		filteredRegions,
		filteredCities,

		getStateByCity: AddressUtilsMultiCity.getStateByCity,
		getMacroregionByCity: AddressUtilsMultiCity.getMacroregionByCity,
		getRegionInfo: AddressUtilsMultiCity.getRegionInfo,
		formatAddressDisplay: AddressUtilsMultiCity.formatAddressDisplay,
		isValidAddress: AddressUtilsMultiCity.isValidAddress,
		getRegionContext: AddressUtilsMultiCity.getRegionContext,
		getCityContext: AddressUtilsMultiCity.getCityContext,
		getMacroregionContext: AddressUtilsMultiCity.getMacroregionContext,
		getAddressStats: AddressUtilsMultiCity.getAddressStats,
		searchAddresses: AddressUtilsMultiCity.searchAddresses,
		getAllStates: AddressUtilsMultiCity.getAllStates,
		getCitiesByState: AddressUtilsMultiCity.getCitiesByState,
		getMacroregionsByState: AddressUtilsMultiCity.getMacroregionsByState,
		getRegionsByState: AddressUtilsMultiCity.getRegionsByState,
		getRegionsByCity: AddressUtilsMultiCity.getRegionsByCity,
		getRegionsByMacroregion: AddressUtilsMultiCity.getRegionsByMacroregion
	};
}
